import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useGame } from '@/hooks/useGamePlay';
import { useUsers } from '@/hooks/useShared';
import { useActiveGame, useAppConfig } from '@/hooks/useShared';
import type { Game } from '@/lib/types/game';
import { 
  resetSettlement, 
  reopenGame,
  unlockStep,
  deriveSettlementView, 
  finalizeChipCounts, 
  migrateLegacySettlement
} from '@/hooks/useSettlement';
import { addBuyIn } from '@/hooks/useGamePlay';
import { useAuth } from '@/hooks/useAuth';
import { canManageUsers } from '@/lib/roles';
import { formatRuntimeGameNumber } from '@/lib/runtime-env';
import { toast } from 'sonner';
import NotificationService from '@/services/notification.service';
import { LoadingSpinner, EmptyState, Banner, Modal } from '@/components/ui';
import { DigitalTransfersStep } from '@/components/settlement/DigitalTransfersStep';
import { CashDistributionStep } from '@/components/settlement/CashDistributionStep';
import { formatDate, formatCurrency } from '@/lib/utils';

function stackDraftKey(gameId: string): string {
  return `pk:settlement:stack-draft:${gameId}`;
}

function hasFinalizedChipCounts(game: Game): boolean {
  return Boolean(game.settlement?.milestones?.chip_counts_finalized);
}

function buildPersistedStacks(game: Game): Record<string, string> {
  const stacks: Record<string, string> = {};
  game.players.forEach(p => {
    const stack = game.settlement?.player_inputs[p.user_id]?.final_stack;
    stacks[p.user_id] = stack && stack > 0 ? stack.toString() : "";
  });
  return stacks;
}

function readStackDraft(game: Game): Record<string, string> | null {
  if (hasFinalizedChipCounts(game) || typeof window === 'undefined') return null;

  const rawDraft = window.sessionStorage.getItem(stackDraftKey(game.id));
  if (!rawDraft) return null;

  try {
    const parsed = JSON.parse(rawDraft) as Record<string, unknown>;
    const playerIds = new Set(game.players.map(player => player.user_id));
    const draft: Record<string, string> = buildPersistedStacks(game);
    Object.entries(parsed).forEach(([userId, value]) => {
      if (playerIds.has(userId) && typeof value === 'string') {
        draft[userId] = value;
      }
    });
    return draft;
  } catch {
    window.sessionStorage.removeItem(stackDraftKey(game.id));
    return null;
  }
}

function writeStackDraft(gameId: string, stacks: Record<string, string>): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(stackDraftKey(gameId), JSON.stringify(stacks));
}

function clearStackDraft(gameId: string): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(stackDraftKey(gameId));
}

function buildInitialStacks(game: Game): Record<string, string> {
  return readStackDraft(game) ?? buildPersistedStacks(game);
}

export function SettlementPage({ game: propGame }: { game?: Game | null }) {
  const { gameId } = useParams<{ gameId: string }>();
  
  const { config } = useAppConfig();
  const currencySymbol = config?.currency_symbol || '$';
  
  const { game: specificGame, loading: specificLoading } = useGame(gameId || undefined);
  const { activeGame, loading: activeLoading } = useActiveGame();
  
  const game = propGame || (gameId ? specificGame : activeGame);
  const gameLoading = propGame ? false : (gameId ? specificLoading : activeLoading);
  const { users, loading: usersLoading } = useUsers();
  const { user, loading: authLoading } = useAuth();
  const [resetting, setResetting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const currentView = game ? deriveSettlementView(game) : 'chip_counts';
  
  const [isCorrectionModalOpen, setCorrectionModalOpen] = useState(false);
  const [correctionPlayerId, setCorrectionPlayerId] = useState<string>('');
  const [correctionAmount, setCorrectionAmount] = useState<string>('');
  const [correctionType, setCorrectionType] = useState<'cash' | 'digital'>('digital');
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  
  const [localStacks, setLocalStacks] = useState<Record<string, string>>(() => (game ? buildInitialStacks(game) : {}));
  const localStacksDirtyRef = useRef(false);
  const initializedStacksGameIdRef = useRef<string | null>(game?.id ?? null);
    
  useEffect(() => {
    if (!game) return;

    if (hasFinalizedChipCounts(game)) {
      clearStackDraft(game.id);
      localStacksDirtyRef.current = false;
      initializedStacksGameIdRef.current = game.id;
      setLocalStacks(buildPersistedStacks(game));
      return;
    }

    const isNewGame = initializedStacksGameIdRef.current !== game.id;
    if (!isNewGame && localStacksDirtyRef.current) return;

    setLocalStacks(buildInitialStacks(game));
    initializedStacksGameIdRef.current = game.id;
    localStacksDirtyRef.current = false;
  }, [game]);

  useEffect(() => {
    if (!game || !game.id) return;
    
    migrateLegacySettlement(game.id).catch((err: any) => {
      console.error('Failed to migrate legacy settlement:', err);
    });
  }, [game?.id]);
  
  if (gameLoading || usersLoading || authLoading) return <LoadingSpinner message="Loading Settlement..." />;
  if (!game) return null;
  
  const isAdmin = canManageUsers(user);
  const isSettled = game.status === 'settled';
  
  const players = game.players
    .filter(p => !p.is_spectator)
    .map(p => {
      const userData = users.find(u => u.id === p.user_id);
      return {
        ...p,
        name: userData?.name || p.name || 'Unknown Player'
      };
    });
  
   const totalPot = game.settlement?.player_snapshot.reduce((sum, p) => sum + p.buyIn, 0) || 0;
   const totalInStacks = Object.values(localStacks).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
   const remainingInStacks = totalPot - totalInStacks;

  const isStacksValid = Math.abs(remainingInStacks) < 0.01;
  
  const handleClearAll = () => {
    localStacksDirtyRef.current = true;
    const cleared: Record<string, string> = {};
    game.players.forEach(p => { cleared[p.user_id] = ""; });
    writeStackDraft(game.id, cleared);
    setLocalStacks(cleared);
  };
  
  const handleStackChange = (playerUserId: string, value: string) => {
    localStacksDirtyRef.current = true;
    setLocalStacks(prev => {
      const next = { ...prev, [playerUserId]: value };
      writeStackDraft(game.id, next);
      return next;
    });
  };
    
  const handleReset = async () => {
    if (!isAdmin || !window.confirm('Clear ALL chip counts and cash distribution from the database? This cannot be undone.')) return;
    setResetting(true);
    try {
      await resetSettlement(game.id);
      clearStackDraft(game.id);
      localStacksDirtyRef.current = false;
      setLocalStacks({});
    } catch (err) {
      setError('Failed to reset game.');
    } finally {
      setResetting(false);
    }
  };

  const handleReopen = async () => {
    if (!isAdmin || !window.confirm('Reopening will archive current transactions and reset settlement inputs. Continue?')) return;
    setReopening(true);
    try {
      await reopenGame(game.id, user?.id ?? '');
      window.location.reload(); // Reload to refresh game state and view
    } catch (err: any) {
      setError(err?.message || 'Failed to reopen game.');
    } finally {
      setReopening(false);
    }
  };

  const handlePublishToTelegram = async (includeEmail: boolean = false, transactions?: any[]) => {
    if (!isAdmin) return;

    try {
      const totalPot = game.settlement?.player_snapshot.reduce((sum, p) => sum + p.buyIn, 0) || 0;
      const transfers = game.settlement?.player_inputs
        ? Object.entries(game.settlement.player_inputs).map(([uid, input]) => {
            const p = game.players.find(pl => pl.user_id === uid);
            return `${p?.name || uid}: ${formatCurrency(input.final_stack, currencySymbol)}`;
          }).join('\\n')
        : 'No transfer data available';

      const summary = `Total Pot: ${formatCurrency(totalPot, currencySymbol)}\\n\\nFinal Stacks:\\n${transfers}`;

      const activeChatConfig = config?.telegram_chat_configs?.find(
        c => c.id === config?.telegram_active_chat_id
      );
      const activeChatId = activeChatConfig ? Number(activeChatConfig.chat_id) : undefined;

      if (activeChatId === undefined) {
        toast.error('No Telegram chat configured — publish skipped.');
        return;
      }

      const txPayload = (transactions ?? []).map(tx => {
        const fromUser = users.find(u => u.id === tx.from_user_id);
        const fromPlayer = game.players.find(p => p.user_id === tx.from_user_id);
        const toUser = users.find(u => u.id === tx.to_user_id);
        const toPlayer = game.players.find(p => p.user_id === tx.to_user_id);
        return {
          id: tx.id,
          fromUserId: tx.from_user_id,
          toUserId: tx.to_user_id,
          amount: tx.amount,
          status: tx.status,
          payerName: fromUser?.name || fromPlayer?.name || 'Payer',
          payeeName: toUser?.name || toPlayer?.name || 'Payee',
          payeeBeemId: toUser?.beem_id ?? null,
        };
      });

      await NotificationService.sendEvent({
        type: 'SETTLEMENT_PUBLISHED',
        payload: {
          gameId: game.id,
          gameNumber: formatRuntimeGameNumber(game.gameNumber),
          settlementSummary: summary,
          includeEmail,
          chatId: activeChatId,
          transactions: txPayload,
        },
      });
      toast.success('Settlement published to Telegram!');
    } catch (err: any) {
      setError(err?.message || 'Failed to publish settlement.');
    }
  };

  const handleBack = async (view: 'chip_counts' | 'cash_distribution' | 'digital_transfers') => {
    if (!isAdmin) return;
    try {
      await unlockStep(game.id, user?.id ?? '', view);
    } catch (err: any) {
      setError(err?.message || 'Failed to navigate back.');
    }
  };

  const handleCorrection = async () => {
    if (!correctionPlayerId || parseFloat(correctionAmount) === 0 || isNaN(parseFloat(correctionAmount))) return;
    
    setCorrectionSubmitting(true);
    setCorrectionError(null);
    try {
      await addBuyIn(
        game.id,
        correctionPlayerId,
        parseFloat(correctionAmount),
        correctionType,
        user?.id ?? '',
        true
      );
      setCorrectionModalOpen(false);
      setCorrectionAmount('');
      setCorrectionPlayerId('');
    } catch (err: any) {
      setCorrectionError(err?.message || 'Failed to apply buy-in correction.');
    } finally {
      setCorrectionSubmitting(false);
    }
  };
  
  const syncStacks = async () => {
    if (!isAdmin) return;
    setCalculating(true);
    try {
      const stacks: Record<string, number> = {};
      for (const [uid, stackStr] of Object.entries(localStacks)) {
        stacks[uid] = parseFloat(stackStr) || 0;
      }
      
       await finalizeChipCounts(game.id, user?.id ?? '', stacks, false);
       clearStackDraft(game.id);
       localStacksDirtyRef.current = false;

       // Send GAME_ENDED notification
       const results = game.players.map(player => {
         const finalStack = stacks[player.user_id] || 0;
         const net = finalStack - player.total_bought_in;
         const userData = users.find(u => u.id === player.user_id);
         return {
           name: userData?.name || player.name,
           telegram_username: userData?.telegram_username || null,
           net
         };
        }).sort((a, b) => b.net - a.net);

        const activeChatConfig = config?.telegram_chat_configs?.find(
          c => c.id === config?.telegram_active_chat_id
        );
        const activeChatId = activeChatConfig ? Number(activeChatConfig.chat_id) : undefined;

        if (activeChatId === undefined) {
          return;
        }

         await NotificationService.sendEvent({
           type: 'GAME_ENDED',
           payload: {
             gameId: game.id,
             gameNumber: formatRuntimeGameNumber(game.gameNumber),
             results,
             chatId: activeChatId,
           }
         });
    } catch (err: any) {
       setError(err?.message || 'Failed to sync stacks.');
     } finally {
       setCalculating(false);
     }
   };

  
  if (game.status === 'settled' && !game.settlement) {
    return (
      <div className="min-h-screen bg-[#f5f7f8] pb-[180px]">
        <div className="w-full max-w-[400px] min-w-0 mx-auto pt-4">
          <EmptyState icon="⚠️" title="Error" description="Settlement data missing for this game." />
        </div>
      </div>
    );
  }
  
  if (game.status !== 'chip_entry' && game.status !== 'settling' && game.status !== 'settled') {
    return (
      <div className="min-h-screen bg-[#f5f7f8] pb-[180px]">
        <div className="w-full max-w-[400px] min-w-0 mx-auto pt-4">
          <EmptyState icon="📋" title="Settlement Not Active" description="Settlement will appear here after the game ends." />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#f5f7f8] pb-[180px] flex flex-col">
      <div className="w-full max-w-[400px] min-w-0 mx-auto pt-4 flex flex-col flex-1">
        {/* Header */}
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#006855] text-white flex items-center justify-center text-[14px] font-extrabold">PK</div>
            <span className="text-base font-bold text-[#2c2f30]">Poker Knights</span>
          </div>

             <div className="flex items-center gap-3">
             </div>

        </div>
  
        {/* Hero Card */}
        <div className="bg-[#e8f5f0] border border-[#0068551f] rounded-2xl p-[12px_18px] mb-2">
          <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#006855] opacity-70 mb-1">
            {currentView === 'chip_counts' ? '1. Chip Counts' : currentView === 'cash_distribution' ? '2. Cash Distribution' : 'Game Settlement'}
          </div>
          <div className="flex items-baseline gap-2 mb-1.5 flex-wrap">
            <span className="text-[22px] font-extrabold text-[#2c2f30]">{game.venue || 'Poker Night'}</span>
            <span className="text-[13px] text-[#595c5d] font-medium">{formatDate(game.date)}</span>
          </div>
          <div className="flex gap-4 text-xs text-[#006855] opacity-90">
            <div className="flex gap-1">
              <span className="font-medium opacity-80">Total Pot:</span>
              <span className="font-black">{formatCurrency(game.settlement?.player_snapshot.reduce((sum, p) => sum + p.buyIn, 0) || 0, currencySymbol)}</span>
            </div>
            <div className="flex gap-1">
              <span className="font-medium opacity-80">Cash Pot:</span>
              <span className="font-semibold">{formatCurrency(game.settlement?.player_snapshot.reduce((sum, p) => sum + (p.cashIn || 0), 0) || 0, currencySymbol)}</span>
            </div>
          </div>
        </div>
  
        {/* Step Progress */}
        {!isSettled && isAdmin && (
          <div className="flex gap-2 mb-2 px-1">
            <div className={`flex-1 h-1 rounded-full ${currentView !== 'chip_counts' || currentView === 'chip_counts' ? 'bg-[#006855]' : 'bg-[#0068551a]'}`} />
            <div className={`flex-1 h-1 rounded-full ${currentView === 'cash_distribution' || currentView === 'digital_transfers' || currentView === 'completed' ? 'bg-[#006855]' : 'bg-[#0068551a]'}`} />
            <div className={`flex-1 h-1 rounded-full ${currentView === 'digital_transfers' || currentView === 'completed' ? 'bg-[#006855]' : 'bg-[#0068551a]'}`} />
          </div>
        )}
  
        {error && <Banner type="error" className="mb-4">{error}</Banner>}
  
        {game.status === 'settled' ? (
          <div className="mt-4">
            <div className="mb-3 mt-3">
              <div className="text-[12px] font-bold uppercase tracking-wider text-[#595c5d]">Final Pot Distribution</div>
            </div>
            {players.map((player) => {
              const final_stack = game.settlement?.player_inputs[player.user_id]?.final_stack || 0;
              const net = final_stack - player.total_bought_in;
              const initial = (player.name || 'P').charAt(0).toUpperCase();
              return (
                <div key={player.user_id} className="bg-white rounded-xl p-[12px_14px] mb-1.5 flex items-center border border-[#e5e9ea]">
                  <div className="w-9 h-9 rounded-full bg-[#006855] flex items-center justify-center mr-3 text-[15px] font-extrabold text-white shrink-0">{initial}</div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#2c2f30]">
                    {player.name}
                    {player.left_early && (
                      <span className="ml-2 text-[10px] font-bold uppercase text-[#b31b25] bg-[#b31b25]/10 px-1.5 py-0.5 rounded">Left Early</span>
                    )}
                  </div>
                    <div className="text-[11px] text-[#595c5d] mt-0.25">
                      Buy-in: {formatCurrency(player.total_bought_in, currencySymbol)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-[#2c2f30]">{formatCurrency(final_stack, currencySymbol)}</div>
                    <div className={`text-[11px] font-bold mt-0.25 ${net >= 0 ? 'text-[#006855]' : 'text-[#b31b25]'}`}>
                      {net >= 0 ? '+' : ''}{formatCurrency(net, currencySymbol)}
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="relative bottom-0 mt-6 w-full max-w-[400px] px-4 py-3 bg-transparent backdrop-blur-none z-10">
                <div className="max-w-[400px] mx-auto">
                  <div className="flex justify-between items-center mb-2 px-1">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#595c5d]">Total Distributed</span>
                    <span className="text-base font-extrabold text-[#006855]">{formatCurrency(totalPot, currencySymbol)}</span>
                  </div>
                  {isAdmin && (
                    <button 
                      onClick={handleReopen}
                      disabled={reopening}
                      className="w-full bg-white text-[#006855] border border-[#006855] rounded-2xl py-4 px-6 text-xs font-extrabold uppercase tracking-[0.2em] cursor-pointer text-center flex items-center justify-center gap-2 mt-3"
                    >
                      {reopening ? 'Reopening...' : 'Reopen Game'}
                    </button>
                  )}
                </div>
            </div>
          </div>
        ) : (
          <>
             {/* View: Chip Counts */}
             {currentView === 'chip_counts' && (
               <>
                   <div className="mb-2 mt-1 flex justify-between items-end">
                    {isAdmin && (
                      <button 
                        onClick={() => setCorrectionModalOpen(true)}
                        className="bg-white border border-[#e5e9ea] text-[#595c5d] px-2 py-1 text-[9px] font-extrabold uppercase tracking-wider rounded-[10px] cursor-pointer"
                      >
                        + Correct
                      </button>
                    )}
                    <div className="flex gap-3">
                      <button 
                        onClick={handleClearAll}
                        className="bg-white border border-[#e5e9ea] text-[#595c5d] px-2 py-1 text-[9px] font-extrabold uppercase tracking-wider rounded-[10px] cursor-pointer"
                      >
                        Clear All
                      </button>
                     <button 
                       onClick={handleReset}
                       disabled={resetting}
                       className={`bg-transparent border-none px-1 text-[10px] font-extrabold text-[#b31b25] uppercase tracking-wider ${resetting ? 'cursor-default opacity-50' : 'cursor-pointer opacity-100'}`}
                     >
                       {resetting ? 'Resetting...' : 'DB Reset'}
                     </button>
                   </div>
                 </div>
                 
                 {players.map((player) => {
                   const initial = (player.name || 'P').charAt(0).toUpperCase();
                   return (
                     <div key={player.user_id} className="bg-white rounded-xl p-[12px_14px] mb-1.5 flex items-center border border-[#e5e9ea]">
                       <div className="w-9 h-9 rounded-full bg-[#006855] flex items-center justify-center mr-3 text-[15px] font-extrabold text-white shrink-0">{initial}</div>
                       <div className="flex-1">
                      <div className="text-sm font-semibold text-[#2c2f30]">
                        {player.name}
                        {player.left_early && (
                          <span className="ml-2 text-[10px] font-bold uppercase text-[#b31b25] bg-[#b31b25]/10 px-1.5 py-0.5 rounded">Left Early</span>
                        )}
                      </div>
                          <div className="text-[11px] text-[#595c5d] mt-0.25">
                            Total Buy-in: {formatCurrency(player.total_bought_in, currencySymbol)}
                           <br />
                           (💵{formatCurrency(player.cash_in || 0, currencySymbol)} / 📱{formatCurrency((player.total_bought_in || 0) - (player.cash_in || 0), currencySymbol)})
                         </div>
                       </div>
                        {isAdmin ? (
                          <div className="ml-auto flex items-center gap-2">
                             <div className="flex items-center gap-1.5">
                               <span className="text-sm font-bold text-[#595c5d]">{currencySymbol}</span>
                               <input
                                 type="number"
                                 inputMode="decimal"
                                 placeholder="0.00"
                                 value={localStacks[player.user_id] ?? ''}
                                 onChange={(e) => handleStackChange(player.user_id, e.target.value)}
                                 className="w-[85px] bg-[#f5f7f8] border border-[#e5e9ea] rounded-lg px-2.5 py-2 text-[15px] font-bold text-[#2c2f30] text-right outline-none"
                               />
                             </div>

                          </div>
                        ) : (
                         <div className="text-sm font-semibold text-[#2c2f30]">{formatCurrency(game.settlement?.player_inputs[player.user_id]?.final_stack || 0, currencySymbol)}</div>
                       )}
                     </div>
                   );
                 })}
                  {isAdmin && (
                    <div className="fixed w-full max-w-[400px] left-1/2 -translate-x-1/2 px-4 py-3 bg-transparent z-10" style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}>
                      <div className="max-w-[400px] mx-auto flex flex-col items-center gap-3">
                        <div className="flex justify-between items-center w-full px-1">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-[#595c5d]">Remaining to Account</span>
                          <span className={`text-base font-extrabold ${!isStacksValid ? 'text-[#b31b25]' : 'text-[#006855]'}`}>
                            {remainingInStacks > 0 ? '+' : ''}{formatCurrency(remainingInStacks, currencySymbol)}
                          </span>
                        </div>
                        <button
                          onClick={syncStacks}
                          disabled={calculating || !isStacksValid}
                          style={{
                            width: '70%',
                            height: 44,
                            background: 'linear-gradient(135deg, #008a6d, #006855)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 22,
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            fontSize: 13,
                            cursor: (calculating || !isStacksValid) ? 'not-allowed' : 'pointer',
                            boxShadow: '0 4px 10px rgba(0,104,85,0.3)',
                            opacity: (calculating || !isStacksValid) ? 0.3 : 1,
                          }}
                        >
                          {calculating ? 'SYNCING...' : (isStacksValid ? ((game.settlement?.player_snapshot.reduce((sum, p) => sum + (p.cashIn || 0), 0) || 0) > 0 ? 'NEXT: CASH' : 'CALCULATE') : 'DISCREPANCY')}
                        </button>
                      </div>
                    </div>
                  )}
               </>
             )}

            {/* View: Cash Distribution */}
            {currentView === 'cash_distribution' && (
              <CashDistributionStep 
                game={game} 
                onNext={() => {}} // Now driven by milestones
                onBack={() => handleBack('chip_counts')}
              />
            )}
            {/* View: Digital Transfers */}
            {currentView === 'digital_transfers' && (
              <DigitalTransfersStep 
                game={game} 
                onBack={() => handleBack('cash_distribution')}
                onPublish={handlePublishToTelegram}
              />
            )}

             {/* View: Completed */}
             {currentView === 'completed' && (
               <div className="text-center py-10 px-5">
                 <div className="text-[48px] mb-4">🎉</div>
                 <div className="text-xl font-extrabold text-[#006855] mb-2">Settlement Completed!</div>
                 <div className="text-sm text-[#595c5d] mb-6">
                   All transfers have been posted and the game is officially settled.
                 </div>
 
                 <button 
                   onClick={() => { if (window.confirm('Are you sure you want to dismiss this game?')) { window.location.href = '/'; } }}
                   className="w-full bg-white text-[#595c5d] border border-[#e5e9ea] rounded-2xl py-3.5 px-6 text-[11px] font-extrabold uppercase tracking-wider cursor-pointer mt-3 text-center"
                 >
                   Return to Home
                 </button>
               </div>
             )}

          </>
        )}
  
        <Modal 
           isOpen={isCorrectionModalOpen} 
           onClose={() => {
             if (!correctionSubmitting) {
               setCorrectionModalOpen(false);
               setCorrectionError(null);
             }
           }} 
           title="Correct Buy-In"
         >
           <div className="flex flex-col gap-4">
             <div>
               <div className="text-[11px] font-bold text-[#595c5d] mb-2 uppercase tracking-wider">Select Player</div>
               <select 
                 value={correctionPlayerId}
                 onChange={(e) => setCorrectionPlayerId(e.target.value)}
                 disabled={correctionSubmitting}
                 className="w-full p-3 rounded-lg border border-[#e5e9ea] text-sm font-semibold outline-none bg-[#f5f7f8]"
               >
                 <option value="">-- Select Player --</option>
                 {players.map(p => (
                   <option key={p.user_id} value={p.user_id}>{p.name}</option>
                 ))}
               </select>
             </div>
 
             <div className="flex gap-3">
               <div className="flex-1">
                 <div className="text-[11px] font-bold text-[#595c5d] mb-2 uppercase tracking-wider">Amount</div>
                 <div className="flex items-center gap-1">
                   <span className="text-sm font-bold text-[#595c5d]">{currencySymbol}</span>
                   <input 
                     type="number"
                              inputMode="decimal"
                              placeholder="0.00"
                              value={correctionAmount}
                              onChange={(e) => setCorrectionAmount(e.target.value)}
                              disabled={correctionSubmitting}
                              className="w-full p-3 rounded-lg border border-[#e5e9ea] text-sm font-semibold outline-none bg-[#f5f7f8]"
                    />
                 </div>
               </div>
               <div className="flex-1">
                 <div className="text-[11px] font-bold text-[#595c5d] mb-2 uppercase tracking-wider">Type</div>
                 <select 
                   value={correctionType}
                   onChange={(e) => setCorrectionType(e.target.value as 'cash' | 'digital')}
                   disabled={correctionSubmitting}
                   className="w-full p-3 rounded-lg border border-[#e5e9ea] text-sm font-semibold outline-none bg-[#f5f7f8]"
                 >
                   <option value="digital">📱 Digital</option>
                   <option value="cash">💵 Cash</option>
                 </select>
               </div>
             </div>
 
             {correctionError && (
               <div className="p-3 bg-[#fef2f2] border border-[#fecaca] rounded-lg text-[#b31b25] text-xs font-semibold">
                 {correctionError}
               </div>
             )}
 
             <button 
               onClick={handleCorrection}
               disabled={correctionSubmitting || !correctionPlayerId || parseFloat(correctionAmount) === 0 || isNaN(parseFloat(correctionAmount))}
               className={`w-full bg-[#006855] text-white border-none rounded-xl py-4 text-xs font-extrabold uppercase tracking-wider transition-all duration-200 ${ (correctionSubmitting || !correctionPlayerId || parseFloat(correctionAmount) === 0 || isNaN(parseFloat(correctionAmount))) ? 'cursor-default opacity-50' : 'cursor-pointer opacity-100' }`}
             >
               {correctionSubmitting ? 'Applying Changes...' : 'Apply Correction'}
             </button>
           </div>
         </Modal>
   
         {/* Player View Banner */}
         {!isAdmin && !isSettled && (
           <div className="mt-6">
             <Banner type="info">
                An admin is currently settling the game. Final transfers will appear here once calculated.
             </Banner>
           </div>
         )}

      </div>
    </div>
  );
}
